(() => {
  const rootSelector = '[data-zb-for-you], [data-zb-collection-design-grid]';
  if (window.ZBDesignGalleries) {
    window.ZBDesignGalleries.init(document);
    return;
  }

  const roots = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initRoot = (root) => {
    if (roots.has(root)) return;
    const controller = new AbortController();
    const states = new WeakMap();
    const pending = new Set();
    let frame = 0;
    let observer;

    const setActive = (state, index) => {
      if (state.active === index) return;
      const target = state.slides[index];
      const previousDot = state.dots[state.active];
      const nextDot = state.dots[index];
      previousDot?.classList.remove('is-active');
      previousDot?.setAttribute('aria-current', 'false');
      nextDot?.classList.add('is-active');
      nextDot?.setAttribute('aria-current', 'true');
      if (state.label && state.label.textContent !== target.dataset.design) {
        state.label.textContent = target.dataset.design || '';
      }
      const url = target.dataset.url || target.getAttribute('href');
      state.destinations.forEach((link) => {
        if (url && link.getAttribute('href') !== url) link.setAttribute('href', url);
      });
      state.active = index;
      state.card.dataset.activeDesignIndex = String(index);
    };

    const initCard = (card) => {
      if (!card || !root.contains(card)) return null;
      if (states.has(card)) return states.get(card);
      const track = card.querySelector('[data-design-track]');
      const slides = Array.from(card.querySelectorAll('[data-design-slide]'));
      if (!track || !slides.length) return null;

      const state = {
        card, track, slides, dots: [], active: -1,
        label: card.querySelector('[data-active-design]'),
        destinations: Array.from(card.querySelectorAll('[data-card-destination]'))
      };
      const dotsWrap = card.querySelector('[data-design-dots]');
      if (dotsWrap && slides.length > 1) {
        const fragment = document.createDocumentFragment();
        slides.forEach((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'zb-for-you-card__dot';
          dot.dataset.designDot = String(index);
          dot.setAttribute('aria-label', 'Show image ' + (index + 1) + ' of ' + slides.length + ' for ' + (slide.dataset.design || 'this product'));
          fragment.appendChild(dot);
          state.dots.push(dot);
        });
        dotsWrap.replaceChildren(fragment);
      }
      states.set(card, state);
      setActive(state, 0);
      card.dataset.galleryReady = 'true';
      observer?.unobserve(card);
      return state;
    };

    const move = (state, index) => {
      const targetIndex = (index + state.slides.length) % state.slides.length;
      // Read geometry before updating controls to avoid write/read layout cycles.
      const left = state.slides[targetIndex].offsetLeft - state.track.offsetLeft;
      setActive(state, targetIndex);
      state.track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    const onClick = (event) => {
      const button = event.target.closest('[data-design-previous], [data-design-next], [data-design-dot]');
      if (!button || !root.contains(button)) return;
      const state = initCard(button.closest('[data-for-you-card]'));
      if (!state) return;
      let index = Number(button.dataset.designDot);
      if (button.hasAttribute('data-design-previous')) index = state.active - 1;
      if (button.hasAttribute('data-design-next')) index = state.active + 1;
      if (!Number.isInteger(index)) return;
      event.preventDefault();
      move(state, index);
    };

    const onKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!event.target.matches('[data-design-track]')) return;
      const state = initCard(event.target.closest('[data-for-you-card]'));
      if (!state) return;
      event.preventDefault();
      move(state, state.active + (event.key === 'ArrowRight' ? 1 : -1));
    };

    const onScroll = (event) => {
      if (!event.target.matches?.('[data-design-track]')) return;
      const state = initCard(event.target.closest('[data-for-you-card]'));
      if (!state) return;
      pending.add(state);
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        // Each slide fills the track. One width read replaces scanning every photo.
        const updates = Array.from(pending, (item) => {
          const width = item.track.clientWidth;
          const index = width > 0 ? Math.round(Math.abs(item.track.scrollLeft) / width) : item.active;
          return [item, Math.max(0, Math.min(item.slides.length - 1, index))];
        });
        pending.clear();
        updates.forEach(([item, index]) => setActive(item, index));
      });
    };

    root.addEventListener('click', onClick, { signal: controller.signal });
    root.addEventListener('keydown', onKeydown, { signal: controller.signal });
    root.addEventListener('scroll', onScroll, { capture: true, passive: true, signal: controller.signal });
    root.addEventListener('focusin', (event) => {
      initCard(event.target.closest('[data-for-you-card]'));
    }, { signal: controller.signal });

    // Hidden cards are observed too, so See more and collection filtering need no extra initialization.
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) initCard(entry.target); });
      }, { rootMargin: '300px 0px' });
      root.querySelectorAll('[data-for-you-card]').forEach((card) => observer.observe(card));
    } else {
      root.querySelectorAll('[data-for-you-card]').forEach(initCard);
    }

    roots.set(root, () => {
      controller.abort();
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
      pending.clear();
      roots.delete(root);
    });
  };

  const init = (scope) => {
    if (scope.matches?.(rootSelector)) initRoot(scope);
    scope.querySelectorAll(rootSelector).forEach(initRoot);
  };
  window.ZBDesignGalleries = { init };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(rootSelector)) roots.get(event.target)?.();
    event.target.querySelectorAll(rootSelector).forEach((root) => roots.get(root)?.());
  });
  init(document);
})();
